"use client";

import { useRef, useState, type DragEvent } from "react";
import { Image } from "@/components/ui/image";
import { useMutation } from "convex/react";
import { DollarSign, ImagePlus, Loader2, ScanEye, X, Zap } from "@/components/icons";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { IconButton } from "@/components/ui/icon-button";
import {
  ACCEPTED_UPLOAD_FORMAT_LABEL,
  MAX_FILE_SIZE,
  UPLOAD_ACCEPT_ATTRIBUTE,
} from "@/lib/photos/constants";
import { handleError } from "@/lib/handle-error";
import { ERROR_CODES } from "@shared/errors/app-error";
import { toStorablePhotoFormat } from "@/lib/photos/uploadHandler";
import { resizeImageForUpload } from "@/lib/photos/resize";
import { validateUploadFileType } from "@/lib/photos/validation";
import { readUploadedStorageId } from "@/lib/convex/validateId";
import type { GradingPhoto } from "./grading-session";

const MAX_PHOTOS = 3;

interface PhotoUploadSectionProps {
  photos: Array<GradingPhoto>;
  onPhotosChange: (photos: Array<GradingPhoto>) => void;
}

function rejectionReason(file: File): string | null {
  if (!validateUploadFileType(file).valid)
    return `${file.name}: unsupported format`;
  if (file.size > MAX_FILE_SIZE)
    return `${file.name}: file too large (max 25MB)`;
  return null;
}

function uploadFailureMessage(error: unknown, file: File): string {
  const handled = handleError(error, { display: "silent" });
  return handled.code === ERROR_CODES.RATE_LIMIT && handled.message
    ? handled.message
    : `Failed to upload ${file.name}`;
}

async function storeGradingPhoto(
  file: File,
  uploadUrl: string,
): Promise<{ storageId: Id<"_storage">; image: Blob }> {
  const image = await resizeImageForUpload(await toStorablePhotoFormat(file));
  const response = await fetch(uploadUrl, {
    method: "POST",
    headers: { "Content-Type": image.type },
    body: image,
  });
  const storageId = response.ok
    ? readUploadedStorageId(await response.json())
    : null;
  if (!storageId) throw new Error("Upload failed");
  return { storageId, image };
}

function usePhotoUploads({ photos, onPhotosChange }: PhotoUploadSectionProps) {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const generateUploadUrl = useMutation(
    api.ai.grading.mutations.generateGradingUploadUrl,
  );

  const addFiles = async (files: Array<File>) => {
    if (uploading) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setMessage(`Maximum ${MAX_PHOTOS} photos allowed.`);
      return;
    }
    setMessage(null);
    setUploading(true);
    const added: Array<GradingPhoto> = [];
    const rejected: Array<string> = [];
    for (const file of files.slice(0, remaining)) {
      const reason = rejectionReason(file);
      if (reason) {
        rejected.push(reason);
        continue;
      }
      try {
        const { storageId, image } = await storeGradingPhoto(
          file,
          await generateUploadUrl({}),
        );
        added.push({ storageId, previewUrl: URL.createObjectURL(image) });
      } catch (error) {
        rejected.push(uploadFailureMessage(error, file));
      }
    }
    if (added.length > 0) onPhotosChange([...photos, ...added]);
    if (rejected.length > 0) setMessage(rejected.join(" · "));
    setUploading(false);
  };

  const remove = (photo: GradingPhoto) => {
    URL.revokeObjectURL(photo.previewUrl);
    onPhotosChange(photos.filter((candidate) => candidate !== photo));
  };

  return { uploading, message, addFiles, remove };
}

export function PhotoUploadSection(props: PhotoUploadSectionProps) {
  const uploads = usePhotoUploads(props);
  return (
    <div className="space-y-4">
      <DropZone uploading={uploads.uploading} onFiles={uploads.addFiles} />
      {uploads.message && (
        <p
          role="alert"
          aria-live="polite"
          className="text-sm text-destructive-strong"
        >
          {uploads.message}
        </p>
      )}
      {props.photos.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {props.photos.map((photo, index) => (
            <PhotoPreviewTile
              key={photo.storageId}
              src={photo.previewUrl}
              index={index}
              onRemove={() => uploads.remove(photo)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function useFileDrop(onFiles: (files: Array<File>) => void) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dropHandlers = {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDraggingOver(true);
    },
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDraggingOver(false);
      onFiles(Array.from(event.dataTransfer.files));
    },
  };
  return { isDraggingOver, dropHandlers };
}

function DropZone({
  uploading,
  onFiles,
}: {
  uploading: boolean;
  onFiles: (files: Array<File>) => void;
}) {
  const { isDraggingOver, dropHandlers } = useFileDrop(onFiles);
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className={cn(
        "relative cursor-pointer overflow-hidden rounded-sm border border-dashed transition-colors",
        isDraggingOver
          ? "border-foreground bg-muted"
          : "border-border-strong hover:border-foreground/40 hover:bg-muted/40",
      )}
    >
      <button
        type="button"
        className="w-full p-8 text-center focus-visible:outline-2 focus-visible:outline-ring"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
        {...dropHandlers}
      >
        {uploading ? <UploadingIndicator /> : <DropZonePrompt />}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={UPLOAD_ACCEPT_ATTRIBUTE}
        multiple
        className="hidden"
        onChange={(event) => {
          onFiles(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
    </div>
  );
}

function UploadingIndicator() {
  return (
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Uploading</p>
    </div>
  );
}

const DROP_ZONE_FEATURES = [
  { icon: ScanEye, label: "AI condition grade" },
  { icon: DollarSign, label: "What buyers pay" },
  { icon: Zap, label: "Results in seconds" },
] as const;

function DropZonePrompt() {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="rounded-sm bg-muted p-3">
        <ImagePlus className="size-6 text-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-base font-semibold">
          Drop photos here or click to browse
        </p>
        <p className="text-sm text-muted-foreground">
          Up to {MAX_PHOTOS} photos · {ACCEPTED_UPLOAD_FORMAT_LABEL}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-4 pt-2 text-xs text-muted-foreground">
        {DROP_ZONE_FEATURES.map(({ icon: Icon, label }) => (
          <span key={label} className="flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function PhotoPreviewTile({
  src,
  index,
  onRemove,
}: {
  src: string;
  index: number;
  onRemove: () => void;
}) {
  return (
    <div className="relative aspect-square overflow-hidden rounded-lg border shadow-sm">
      <Image
        src={src}
        alt={`Upload ${index + 1}`}
        fill
        className="object-cover"
        unoptimized
      />
      <IconButton
        size="compact"
        aria-label="Remove photo"
        onClick={onRemove}
        className="absolute right-1 top-1 rounded-full bg-black/60 text-white hover:bg-black/80 hover:text-white"
      >
        <X />
      </IconButton>
    </div>
  );
}
